import _, {compact, isUndefined, omit, pick} from 'lodash';

import {ErrorWithData} from '../../../../libs/shared/helpers/src/types/customErrors';
import {randomString} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {logger} from '../../../../srcCommon/services/logger/logger';
import {offlineDraftMessages} from '../../../../srcCommon/services/offline/offlineDraftMessages';
import {OfflineError} from '../../../../srcCommon/services/offline/offlineError';
import {withTimeoutAsync} from '../../../helpers/asyncHelpers';
import {EmailFormatsEnum} from '../../../helpers/models/emailFormatHelpers';
import {draftMessageHasRawVariables, isDirtyDraftMessage} from '../../../helpers/models/messageHelpers';
import {MessagePatcher} from '../../../helpers/models/messagePatchHelpers';
import {buildSignatureHtml} from '../../../helpers/models/signatureHelpers';
import {hasPersistedStorageAsync} from '../../../helpers/storageHelpers';
import {serializeAttachment} from '../../../models/api/attachmentModel';
import {ApiMessageDataGroup, DataGroupModel} from '../../../models/api/dataGroupModel';
import {
  MessageErrorModel,
  MessageErrorsEnum,
  MessageErrorSourcesEnum,
} from '../../../models/api/messageErrorModel';
import {
  DraftMessageModel,
  MessageModel,
  MessageTypesEnum,
  MessageVisualStatusesEnum,
} from '../../../models/api/messageModel';
import {serializeRecipient} from '../../../models/api/recipientModel';
import {VisualRecipientModel} from '../../../models/api/visualRecipientModel';
import {findMessageComposer} from '../../../services/composers';
import {reserveHandleTimeToSave} from '../../../services/handleTime';
import {putApiAsync} from '../../../services/http/http';
import {
  HttpError,
  HttpNetworkError,
  HttpResponseError,
  HttpTimeoutError,
} from '../../../services/http/httpError';
import {findMessageQueue} from '../../../services/tasks/specializedTaskQueues';
import {AppState} from '../../../store/appState';
import {ThunkAsyncAction} from '../../../store/appThunk';
import {updateDataAction} from '../../../store/data/dataGroupsActions';
import {getOptimisticData} from '../../../store/data/dataSelectors';
import {makeGetVariableInterpolator} from '../../../store/data/messages/messagesSelectors';
import {getSignatures} from '../signatures/signaturesSelectors';
import {catchMessageAlreadySentAsync, MessageVersionOptions} from './messagesHelpers';

const offlineContentLogger = logger.getLogger('offlineContent');
const handleTimeLogger = logger.getLogger('handleTime');

/*
 * Types.
 */

export interface SaveMessageOptions extends MessageVersionOptions {
  patchMessage: MessagePatcher;
  newRecipients?: ReadonlyArray<VisualRecipientModel>;
}

/*
 * Constants.
 */

// Assign a random ID to this session to help detect edit conflicts from another app instance.
const sessionId = randomString(4);

// Don't include Old Front styles on the backend.
const mainStyle = '/* inherit */';

/*
 * Types.
 */

interface SaveMessageResult {
  savedMessage: DraftMessageModel | undefined;
  queueDrainPromise: Promise<void>;
}

/*
 * Action creators.
 */

export function saveMessageAsyncAction(
  messageUid: string,
  options: SaveMessageOptions,
): ThunkAsyncAction<SaveMessageResult> {
  return async (dispatch, getState) => {
    // TODO: Use the new optimistic update system. Probably will require refactoring EmailComposer and MessagePatcher.
    const messageQueue = findMessageQueue(dispatch, getState, messageUid);
    messageQueue.update(buildOptimisticResult(getState(), messageQueue.get(), options));

    await messageQueue.waitForTurnAsync();
    try {
      const resultDataGroup = await trySaveMessageOnlineOrOfflineAsync(
        getState(),
        messageQueue.getResult(),
        options,
      );

      // Make the conversation available on the state immediately (instead of waiting for the queue to drain) so that
      // it works with offline message sending.
      dispatch(updateDataAction(omit(resultDataGroup, 'messages')));

      return {
        savedMessage: findDraftMessageInDataGroup(resultDataGroup, messageUid),
        queueDrainPromise: messageQueue
          .provideResultAsync(pick(resultDataGroup, 'messages'))
          .then(() => undefined),
      };
    } catch (error) {
      messageQueue.reportError();
      throw error;
    }
  };
}

/**
 * Flushes unsaved changes for the given message to the server.
 *
 * @param versionOptions
 *   If provided, used for conflict detection. If not provided, the message version in the app state is used instead.
 */
export function flushMessageAsyncAction(
  messageUid: string,
  versionOptions?: MessageVersionOptions,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    offlineContentLogger.debug('Flushing offline message', {
      message_uid: messageUid,
    });

    // If we don't have access to the composer state while this draft is open in a composer, avoid creating a version
    // conflict with that composer. (Specifically: don't save the draft when we come back online while a composer is
    // open.)
    if (!versionOptions && findMessageComposer(messageUid)) {
      return;
    }

    const messageQueue = findMessageQueue(dispatch, getState, messageUid);
    await messageQueue.waitForTurnAsync();
    const message = messageQueue.getResult();

    // If the message no longer exists, isn't a draft, or has no unsaved changes, we're done.
    if (
      !message ||
      message.visualStatus !== MessageVisualStatusesEnum.DRAFT ||
      (!isDirtyDraftMessage(message) && !draftMessageHasRawVariables(message))
    ) {
      messageQueue.provideResultAsync({});
      return;
    }

    // Perform conflict resolution with the app state, just in case.
    const expectedVersion = versionOptions?.getVersion();
    if (!isUndefined(expectedVersion) && expectedVersion !== message.version) {
      messageQueue.provideResultAsync({
        messages: [
          {
            ...message,
            error: {
              source: MessageErrorSourcesEnum.CLIENT,
              type: MessageErrorsEnum.VERSION_CONFLICT,
            },
          },
        ],
      });
      return;
    }

    try {
      // Try to save online.
      const state = getState();
      const {serverDataGroup, serverMessage} = await saveMessageOnlineAndMaybeReinterpolateAsync(
        message,
        state,
      );

      if (serverMessage && versionOptions?.setVersion) {
        versionOptions.setVersion(serverMessage.version);
      }

      // Update the store.
      dispatch(updateDataAction(omit(serverDataGroup, 'messages')));
      messageQueue.provideResultAsync(pick(serverDataGroup, 'messages'));
    } catch (error) {
      // If we're offline, try again later.
      const isOfflineError = error instanceof HttpNetworkError || error instanceof HttpTimeoutError;
      if (isOfflineError) {
        messageQueue.reportError();
        throw new OfflineError(error);
      }

      // Report non-HTTP errors to Bugsnag.
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }

      // Put the draft in a failed state.
      const failedMessage = {
        ...message,
        error: getMessageError(error),
      };

      // Update the store.
      messageQueue.provideResultAsync({messages: [failedMessage]});
    }
  };
}

/*
 * Helpers.
 */

async function saveMessageOnlineAndMaybeReinterpolateAsync(message: DraftMessageModel, state: AppState) {
  const serverDataGroup = await saveMessageOnlineAsync(state, message);

  // Update the composer state with the new version to avoid conflicts, if necessary.
  const serverMessage = findDraftMessageInDataGroup(serverDataGroup, message.uid);

  // If message is draft at this stage, it means user has hit send before we were able to save the message on the server.
  // This however means we might have some raw variables in the message like `{{message.id}}` which were
  // not interpolated, because message.id was not available yet.
  // So we need to interpolate one more, now with server side data, and save message to the server again
  if (
    message.visualStatus === MessageVisualStatusesEnum.DRAFT &&
    serverMessage &&
    draftMessageHasRawVariables(message)
  ) {
    const interpolator = makeGetVariableInterpolator(() => serverMessage)(state, {});
    const newMessage = {
      ...serverMessage,
      html: interpolator.interpolateDirtyHtml(message.html, {shouldReplacePublicIdPlaceholders: true}),
      subject: interpolator.interpolateText(message.subject, {shouldReplacePublicIdPlaceholders: true}),
      signatureHtml: message.signatureHtml
        ? interpolator.interpolateDirtyHtml(message.signatureHtml, {shouldReplacePublicIdPlaceholders: true})
        : undefined,
    };

    const updatedServerDataGroup = await saveMessageOnlineAsync(state, newMessage);
    const updatedServerMessage = findDraftMessageInDataGroup(updatedServerDataGroup, newMessage.uid);
    return {
      serverMessage: updatedServerMessage,
      serverDataGroup: updatedServerDataGroup,
    };
  }

  return {serverMessage, serverDataGroup};
}

function buildOptimisticResult(
  state: AppState,
  message: MessageModel | undefined,
  options: SaveMessageOptions,
) {
  if (!message || message.visualStatus !== MessageVisualStatusesEnum.DRAFT) {
    throw new Error('A message with this UID could not be found.');
  }

  const data = getOptimisticData(state);
  const patchedMessage = options.patchMessage(data, message);
  if (!patchedMessage) {
    throw new Error('The message could not be patched.');
  }

  return {
    messages: [
      {
        ...patchedMessage,
        version: options.getVersion(),
      },
    ],
    contacts: findContacts(options),
    cards: findCards(options),
  };
}

async function trySaveMessageOnlineOrOfflineAsync(
  state: AppState,
  message: MessageModel | undefined,
  options: SaveMessageOptions,
) {
  if (!message || message.visualStatus !== MessageVisualStatusesEnum.DRAFT) {
    throw new Error('A message with this UID could not be found.');
  }

  // TODO: Should we be using unsaved/optimistic data to generate the signature HTML and such?
  const data = getOptimisticData(state);
  const patchedMessage = options.patchMessage(data, message, {onlyIfLastSave: true});

  // If a new save has been queued, skip.
  if (!patchedMessage) {
    return {};
  }

  try {
    // Try to save the message.
    return await saveMessageOnlineOrOfflineAsync(state, patchedMessage, options);
  } catch (error) {
    // Report non-HTTP errors to Bugsnag.
    if (!(error instanceof HttpError)) {
      reportToBugsnag(error);
    }

    // Put the draft in a failed state.
    const failedMessage = {
      ...patchedMessage,
      error: getMessageError(error),
    };

    return {messages: [failedMessage]};
  }
}

async function saveMessageOnlineOrOfflineAsync(
  state: AppState,
  message: DraftMessageModel,
  options: SaveMessageOptions,
) {
  try {
    // Try to save online.
    const serverDataGroup = await saveMessageOnlineAsync(state, {
      ...message,
      version: options.getVersion(),
    });

    // Update the composer state with the new version to avoid conflicts.
    const serverMessage = findDraftMessageInDataGroup(serverDataGroup, message.uid);
    if (serverMessage && options.setVersion) {
      options.setVersion(serverMessage.version);
    }

    return serverDataGroup;
  } catch (error) {
    // Can we save offline instead?
    const isOfflineError = error instanceof HttpNetworkError || error instanceof HttpTimeoutError;
    if (!isOfflineError) {
      throw error;
    }

    // Try to save offline.
    return await saveMessageOfflineAsync(state, message, error);
  }
}

export async function saveMessageOnlineAsync(state: AppState, message: DraftMessageModel) {
  // Make the API call.
  const serverDataGroup = await saveMessageApiAsync(state, message);

  // Clean up client-side storage. It's OK if this fails, because between messages with different versions the
  // last writer wins anyway.
  removeMessageFromCacheAsync(message).catch((error) => {
    reportToBugsnag(error);
  });

  return serverDataGroup;
}

export async function saveMessageOfflineAsync(
  state: AppState,
  message: DraftMessageModel,
  originalError: unknown,
) {
  // Save the patched message to client-side storage.
  const [hasPersistedStorage] = await Promise.all([
    hasPersistedStorageAsync(),
    saveMessageToCacheAsync(state, message),
  ]);

  // If we don't have persisted storage, re-throw the original error.
  if (!hasPersistedStorage) {
    throw originalError;
  }

  offlineContentLogger.debug('Saved offline message', {
    message_uid: message.uid,
  });

  return {messages: [message]};
}

export function findDraftMessageInDataGroup(
  dataGroup: DataGroupModel,
  messageUid: string,
): DraftMessageModel | undefined {
  return (
    _(dataGroup.messages)
      // map().compact() gives us better type inference.
      .map((m) => m.uid === messageUid && m.visualStatus === MessageVisualStatusesEnum.DRAFT && m)
      .compact()
      .first()
  );
}

async function saveMessageApiAsync(state: AppState, message: DraftMessageModel) {
  // Reserve the handle time increment to be saved.
  const {handleTimeIncrement, cancelUnsavedHandleTime} = reserveHandleTimeToSave(message);
  if (handleTimeIncrement > 3600) {
    handleTimeLogger.info('handleTimeIncrement when saving', {handleTimeIncrement});
  }

  const body = {
    in_reply_to_id: message.inReplyToId,
    author_id: message.authorId,
    from: {channel_id: message.fromChannelId},
    subject: message.subject,
    recipients: message.recipients.map(serializeRecipient),
    attachments: message.attachments.map(serializeAttachment),
    html: message.html,
    text: message.text,
    shared_draft: message.isShared,
    has_quote: !isUndefined(message.quoteHtml),
    canned_answer_id: message.cannedAnswerId,

    quote_html: message.quoteHtml,
    quote_include: message.isQuoteIncluded,
    quote_modified: message.wasQuoteModified,

    forward_html: message.forwardHtml,
    forward_include: message.isForwardIncluded,
    forward_modified: message.wasForwardModified,

    ...buildSignatureRequestBody(state, message),

    main_style: mainStyle,
    default_font_style: message.defaultFontStyle || '',
    format: getMessageFormat(message),
    version: message.version,
    author_session: sessionId,
    handle_time_increment: handleTimeIncrement,

    add_author_ids: message.addAuthorIds,
  };

  try {
    return await catchMessageAlreadySentAsync(message, () =>
      putApiAsync(ApiMessageDataGroup, `${message.url}?include_conversation=true`, body),
    );
  } catch (error) {
    cancelUnsavedHandleTime();

    throw error;
  }
}

function findContacts(options: SaveMessageOptions) {
  const {newRecipients} = options;
  return newRecipients && newRecipients.map((r) => r.contact);
}

function findCards(options: SaveMessageOptions) {
  const {newRecipients} = options;
  return newRecipients && compact(newRecipients.map((r) => r.card));
}

function buildSignatureRequestBody(state: AppState, message: DraftMessageModel) {
  // Find the signature for this message.
  const signatures = getSignatures(state);
  const signature = message.signatureId && signatures[message.signatureId];
  if (!signature) {
    return undefined;
  }

  const interpolator = makeGetVariableInterpolator(() => message)(state, {});
  const signatureHtml = buildSignatureHtml(state, message);

  return {
    signature: {id: signature.id},
    signature_html: signatureHtml,
    signature_include: message.isSignatureIncluded,
    signature_sender_info: signature.senderInfo && interpolator.interpolateText(signature.senderInfo),
    signature_modified: message.wasSignatureModified,
  };
}

function getMessageFormat(message: DraftMessageModel): EmailFormatsEnum | undefined {
  // Non-email messages don't have a format.
  if (message.type !== MessageTypesEnum.EMAIL) {
    return undefined;
  }

  // Email messages MUST have a format to avoid server quirks.
  return message.format || EmailFormatsEnum.HTML;
}

function getMessageError(error: any): MessageErrorModel {
  const type =
    error instanceof HttpResponseError && error.status === 409
      ? MessageErrorsEnum.VERSION_CONFLICT
      : MessageErrorsEnum.CANT_SAVE;
  return {source: MessageErrorSourcesEnum.CLIENT, type};
}

/*
 * Offline drafts.
 */

const cacheTimeout = 15 * 1000;

async function saveMessageToCacheAsync(state: AppState, message: DraftMessageModel) {
  return withTimeoutAsync(
    async () => {
      // Use the optimistic data to include the contacts and cards for new recipients.
      await offlineDraftMessages.addAsync(message, state.data);
    },
    cacheTimeout,
    (elapsedWallTime) => {
      throw new ErrorWithData('Saving offline draft timed out', {
        elapsedWallTime,
        timeout: cacheTimeout,
      });
    },
  );
}

async function removeMessageFromCacheAsync(message: DraftMessageModel) {
  return withTimeoutAsync(
    async () => {
      await offlineDraftMessages.removeAsync(message);
    },
    cacheTimeout,
    (elapsedWallTime) => {
      throw new ErrorWithData('Removing offline draft timed out', {
        elapsedWallTime,
        timeout: cacheTimeout,
      });
    },
  );
}
